/**
 * Carregamento sob demanda dos dados vetoriais de `public/data/`.
 *
 * - `manifest.json` é baixado uma vez (pequeno).
 * - Cada arquivo só é baixado quando uma camada que o usa é ligada; o
 *   resultado (uma `VectorSource` já projetada para o GOES) fica em cache
 *   para o resto da sessão.
 * - Falhas de rede/servidor são repetidas sozinhas com espera crescente
 *   (`RETRY_DELAYS_MS`); se ainda assim falhar, a camada fica em erro e volta
 *   a ser pedida por `retryFailed()` (botão "Tentar de novo" e evento `online`).
 * - Estados de carregamento/erro vão para o store (o painel mostra).
 */
import VectorSource from 'ol/source/Vector';
import type Feature from 'ol/Feature';
import type { Geometry } from 'ol/geom';
import { getTransform } from 'ol/proj';
import { fetchWithRetry, isAbortError, sleep } from '../lib/fetchRetry';
import { GOES_PROJ, registerGoesProjection } from '../map/projection';
import { flatToOlFeatures, toFlatFeatures, type FlatFeature } from './parse';
import type { ParseRequest, ParseResponse } from './parse.worker';
import { useVectorStore } from './store';
import type { DataLayerId, Manifest, ManifestLayer } from './types';

let dataBaseUrl = `${import.meta.env.BASE_URL}data/`;

/** Troca a pasta dos dados (usado pelo harness para testar com fixtures). */
export function setVectorDataBaseUrl(url: string): void {
  dataBaseUrl = url.endsWith('/') ? url : `${url}/`;
  manifestPromise = null;
  dataPromises.clear();
  sources.clear();
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

// ---------------------------------------------------------------------------
// Novas tentativas
// ---------------------------------------------------------------------------

/** Esperas antes da 2ª e da 3ª tentativa (3 tentativas no total). */
export const RETRY_DELAYS_MS: readonly number[] = [1500, 4000];

/** Falha que não melhora com nova tentativa (conteúdo inválido). */
class PermanentError extends Error {}

/** O worker em si falhou (não carregou etc.) — usar a thread principal. */
class WorkerUnavailable extends Error {}

/** Vale tentar de novo? Rede, tempo esgotado e respostas HTTP de erro, sim; conteúdo inválido, não. */
export function isRetryable(err: unknown): boolean {
  return !(err instanceof PermanentError || err instanceof SyntaxError || err instanceof WorkerUnavailable || isAbortError(err));
}

/** Executa `fn` com novas tentativas e espera crescente entre elas. */
export async function withRetry<T>(
  fn: () => Promise<T>,
  delays: readonly number[] = RETRY_DELAYS_MS,
  wait: (ms: number) => Promise<void> = sleep,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= delays.length || !isRetryable(err)) throw err;
      await wait(delays[attempt]);
    }
  }
}

/** Uma tentativa de download de JSON (tempo-limite e HttpError do helper do app). */
function fetchJson(url: string): Promise<unknown> {
  return fetchWithRetry(url, {}, { retries: 0, read: (r) => r.json() as Promise<unknown> });
}

// ---------------------------------------------------------------------------
// Manifest
// ---------------------------------------------------------------------------

let manifestPromise: Promise<Manifest> | null = null;

/** Valida o formato mínimo do manifest (tolerante a campos extras). */
export function validateManifest(json: unknown): Manifest {
  if (!json || typeof json !== 'object' || !('layers' in json)) throw new Error('manifest.json inválido');
  const layers = (json as { layers: unknown }).layers;
  if (!layers || typeof layers !== 'object') throw new Error('manifest.json sem "layers"');
  const out: Manifest['layers'] = {};
  for (const [id, raw] of Object.entries(layers as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue;
    const l = raw as Partial<ManifestLayer>;
    if (typeof l.file !== 'string') continue;
    out[id] = {
      ...l,
      file: l.file,
      format: l.format === 'geojson' ? 'geojson' : 'topojson',
      geometry: l.geometry ?? 'polygon',
    } as ManifestLayer;
  }
  const generatedAt = (json as { generatedAt?: unknown }).generatedAt;
  return { generatedAt: typeof generatedAt === 'string' ? generatedAt : undefined, layers: out };
}

/** Baixa (uma vez) o manifest. Em caso de erro, uma nova chamada tenta de novo. */
export function ensureManifest(): Promise<Manifest> {
  if (manifestPromise) return manifestPromise;
  useVectorStore.getState().setManifestState({ status: 'loading' });
  const url = `${dataBaseUrl}manifest.json`;
  manifestPromise = withRetry(() => fetchJson(url))
    .then((json) => {
      const manifest = validateManifest(json);
      useVectorStore.getState().setManifestState({ status: 'ready' }, manifest);
      return manifest;
    })
    .catch((err: unknown) => {
      manifestPromise = null;
      useVectorStore.getState().setManifestState({ status: 'error', error: errorMessage(err) });
      throw err;
    });
  return manifestPromise;
}

// ---------------------------------------------------------------------------
// Dados
// ---------------------------------------------------------------------------

/**
 * Converte o conteúdo de um arquivo (TopoJSON ou GeoJSON em EPSG:4326) em
 * features OpenLayers na projeção do GOES, na thread atual. Descarta
 * geometrias que caiam fora do disco visível (coordenadas infinitas).
 */
export function parseLayerData(json: unknown, meta: Pick<ManifestLayer, 'format' | 'object'>): Feature<Geometry>[] {
  registerGoesProjection();
  return flatToOlFeatures(toFlatFeatures(json, meta, getTransform('EPSG:4326', GOES_PROJ)));
}

// Web Worker (download + conversão + projeção fora da thread principal).

let worker: Worker | null | undefined;
let requestSeq = 0;
const pending = new Map<number, { resolve: (f: FlatFeature[]) => void; reject: (e: Error) => void }>();

function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  if (typeof Worker === 'undefined') return (worker = null);
  try {
    const w = new Worker(new URL('./parse.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (e: MessageEvent<ParseResponse>) => {
      const p = pending.get(e.data.id);
      if (!p) return;
      pending.delete(e.data.id);
      if (e.data.ok) p.resolve(e.data.features);
      else p.reject(e.data.retryable ? new Error(e.data.error) : new PermanentError(e.data.error));
    };
    w.onerror = () => {
      for (const p of pending.values()) p.reject(new WorkerUnavailable('worker indisponível'));
      pending.clear();
      w.terminate();
      worker = null;
    };
    worker = w;
  } catch {
    worker = null;
  }
  return worker;
}

function requestWorker(w: Worker, url: string, meta: ManifestLayer): Promise<FlatFeature[]> {
  return new Promise<FlatFeature[]>((resolve, reject) => {
    const id = ++requestSeq;
    pending.set(id, { resolve, reject });
    const request: ParseRequest = {
      id,
      url: new URL(url, window.location.href).href,
      meta: { format: meta.format, object: meta.object },
    };
    w.postMessage(request);
  });
}

/** Baixa e converte um arquivo — no worker se possível, senão na thread principal. */
async function fetchAndParse(url: string, meta: ManifestLayer): Promise<Feature<Geometry>[]> {
  const w = getWorker();
  if (w) {
    try {
      return flatToOlFeatures(await withRetry(() => requestWorker(w, url, meta)));
    } catch (err) {
      if (!(err instanceof WorkerUnavailable)) throw err;
    }
  }
  const json = await withRetry(() => fetchJson(url));
  return parseLayerData(json, meta);
}

const dataPromises = new Map<DataLayerId, Promise<VectorSource | null>>();
const sources = new Map<DataLayerId, VectorSource>();

/** Fonte já carregada (ou undefined). */
export function getLoadedSource(id: DataLayerId): VectorSource | undefined {
  return sources.get(id);
}

/**
 * Baixa e prepara um arquivo de dados (uma vez). Resolve `null` quando o
 * arquivo não existe no manifest ou quando falha (o erro fica no store).
 */
export function ensureDataSource(id: DataLayerId): Promise<VectorSource | null> {
  const cached = dataPromises.get(id);
  if (cached) return cached;
  const setState = useVectorStore.getState().setDataState;
  setState(id, { status: 'loading' });
  const promise = ensureManifest()
    .then(async (manifest) => {
      const meta = manifest.layers[id];
      if (!meta) {
        setState(id, { status: 'missing' });
        return null;
      }
      const features = await fetchAndParse(dataBaseUrl + meta.file, meta);
      const source = new VectorSource<Feature<Geometry>>({ features, wrapX: false });
      source.set('dataId', id);
      sources.set(id, source);
      setState(id, { status: 'ready' });
      return source;
    })
    .catch((err: unknown) => {
      dataPromises.delete(id);
      setState(id, { status: 'error', error: errorMessage(err) });
      return null;
    });
  dataPromises.set(id, promise);
  return promise;
}

/**
 * Volta para "ocioso" os arquivos em erro (e o manifest, se falhou): o
 * gerenciador de camadas pede de novo os que estiverem em camadas ligadas.
 * Sem argumento, vale para todos os arquivos com erro.
 */
export function retryFailed(ids?: readonly DataLayerId[]): void {
  const state = useVectorStore.getState();
  const manifestFailed = state.manifestState.status === 'error';
  if (manifestFailed) state.setManifestState({ status: 'idle' });
  const targets = ids ?? (Object.keys(state.data) as DataLayerId[]);
  for (const id of targets) {
    if (state.data[id]?.status === 'error') {
      dataPromises.delete(id);
      state.setDataState(id, { status: 'idle' });
    }
  }
  // Recupera créditos/tamanhos do painel mesmo sem camada ligada.
  if (manifestFailed) void ensureManifest().catch(() => {});
}
